import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { LuExternalLink } from 'react-icons/lu';
import { PersonChip, EmptyState, LoadingSpinner } from 'components/common';
import { categoryLabel, deviceLabel, eventLabel } from './auditLogLabels';
import { eventIcon, iconTileClass } from './auditLogIcons';

// Row borders only and a sticky header: the table scrolls inside its own frame,
// so the column names stay in view however far down the log you go. The page
// sizes that frame (maxHeight, useFitToViewport) so the page itself does not
// scroll as well; without one (phone) the frame grows and the page scrolls.
// border-separate, not collapse: a collapsed border does not travel with a
// sticky cell and the header would lose its bottom line while scrolling.
const TH_CLS = [
  'tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50 tw-px-4 tw-py-2.5 tw-text-left tw-whitespace-nowrap',
  'tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500',
  'tw-border-0 tw-border-b tw-border-solid tw-border-slate-200',
].join(' ');
const TD_CLS = 'tw-px-4 tw-py-3 tw-text-sm tw-align-top tw-border-0 tw-border-b tw-border-solid tw-border-slate-100';

const Actor = ({ row }) => {
  if (!row.user) return <span className="tw-text-slate-500">{row.username || 'System'}</span>;

  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <PersonChip name={row.user.name} fullName={row.user.full_name} email={row.user.email} size="sm" />
      {row.impersonator && (
        <span className="tw-text-[11px] tw-text-amber-700">via {row.impersonator.name} (view as)</span>
      )}
    </div>
  );
};

Actor.propTypes = { row: PropTypes.object.isRequired };

const EventCell = ({ row }) => {
  const Icon = eventIcon(row.action, row.category);
  const failed = row.outcome === 'failure';

  return (
    <div className="tw-flex tw-items-start tw-gap-2.5">
      <span className={`tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-shrink-0 ${iconTileClass(row.outcome)}`}>
        <Icon size={15} aria-hidden="true" />
      </span>
      <div className="tw-min-w-0">
        <div className={`tw-font-medium tw-leading-5 ${failed ? 'tw-text-red-700' : 'tw-text-slate-800'}`}>
          {eventLabel(row.action)}
        </div>
        <div className="tw-text-[11px] tw-text-slate-500">
          {categoryLabel(row.category)}
          {failed && <span className="tw-ml-1.5 tw-font-semibold tw-text-red-600">· Failed</span>}
        </div>
      </div>
    </div>
  );
};

EventCell.propTypes = { row: PropTypes.object.isRequired };

const AuditLogTable = ({ rows, loading, onOpen, maxHeight }) => {
  if (loading && rows.length === 0) return <LoadingSpinner />;
  if (rows.length === 0) return <EmptyState title="No events" description="Nothing matches these filters." />;

  return (
    <div
      className="tw-overflow-auto tw-min-h-[240px] tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white"
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="tw-w-full tw-border-separate tw-border-spacing-0">
        <thead>
          <tr>
            <th className={TH_CLS}>Time</th>
            <th className={TH_CLS}>User</th>
            <th className={TH_CLS}>Event</th>
            <th className={TH_CLS}>Details</th>
            <th className={TH_CLS}>Project</th>
            <th className={TH_CLS}>IP · Device</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="tw-cursor-pointer hover:tw-bg-primary-50/40 focus:tw-outline-none focus-visible:tw-bg-primary-50"
              onClick={() => onOpen(row)}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpen(row); }}
            >
              <td className={`${TD_CLS} tw-whitespace-nowrap tw-text-slate-600`}>
                {row.created_at ? dayjs(row.created_at).format('DD-MMM-YYYY HH:mm:ss') : '—'}
              </td>
              <td className={TD_CLS}><Actor row={row} /></td>
              <td className={TD_CLS}><EventCell row={row} /></td>
              <td className={`${TD_CLS} tw-max-w-[420px] tw-text-slate-700`}>
                <span className="tw-line-clamp-2">{row.comment}</span>
              </td>
              <td className={TD_CLS}>
                {row.project ? (
                  <Link
                    to={`/crm/projects/${row.project.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="tw-group tw-inline-flex tw-items-start tw-gap-1 tw-text-primary-600 hover:tw-text-primary-700 hover:tw-underline"
                    title="Open project in a new tab"
                  >
                    <span>{row.project.name || `#${row.project.id}`}</span>
                    <LuExternalLink size={12} className="tw-mt-1 tw-shrink-0 tw-opacity-50 group-hover:tw-opacity-100" />
                  </Link>
                ) : <span className="tw-text-slate-400">—</span>}
              </td>
              <td className={`${TD_CLS} tw-whitespace-nowrap tw-text-xs tw-text-slate-600`}>
                <div>{row.remote_address || '—'}</div>
                <div className="tw-text-slate-400">{deviceLabel(row.user_agent)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

AuditLogTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  maxHeight: PropTypes.number,
};

AuditLogTable.defaultProps = { loading: false, maxHeight: null };

export default AuditLogTable;
